import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Building2, IdCard, Plus, Search, Trash2, Calendar, AlertTriangle,
  Camera, FileText, ShieldCheck, BadgeCheck, Upload, X, ChevronLeft,
  CheckCircle2, XCircle, Clock,
} from "lucide-react";
import { useTrackedSearch } from "../activityTracker";

const POLL_MS = 15000;
const NATIONALITIES = [
  "Malaysian", "Indonesian", "Bangladeshi", "Nepali", "Indian",
  "Filipino", "Vietnamese", "Myanmar", "Pakistani", "Sri Lankan", "Other",
];
const STATUSES = ["active", "resigned", "terminated", "on_leave"];
const STATUS_LABEL = { active: "Active", resigned: "Resigned",
  terminated: "Terminated", on_leave: "On Leave" };
const STATUS_COLOR = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  resigned: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  terminated: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  on_leave: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

export default function WorkersRegister() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = !!user?.is_owner;
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [tab, setTab] = useState("foreigner");  // foreigner | local | expiring
  const [search, setSearch] = useState("");
  useTrackedSearch("workers", search);
  const [workers, setWorkers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // NEW: for non-owner staff, "delete" opens a request dialog instead. For
  // the owner, we surface any pending requests submitted by others.
  const [requestDeleteTarget, setRequestDeleteTarget] = useState(null);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [reviewTarget, setReviewTarget] = useState(null);  // { mode: 'approve'|'reject', req }
  const [expiring, setExpiring] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const loadDeletionRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/worker-deletion-requests?status=Pending");
      setDeletionRequests(data);
    } catch (_) { /* silent */ }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/workers/companies");
      setCompanies(data);
      if (!selectedCompanyId && data.length > 0) setSelectedCompanyId(data[0].id);
    } catch (_) { /* silent — company list is non-critical */ }
  }, [selectedCompanyId]);

  const loadWorkers = useCallback(async () => {
    if (!selectedCompanyId) { setWorkers([]); return; }
    try {
      const params = { company_id: selectedCompanyId };
      if (tab === "local" || tab === "foreigner") params.worker_type = tab;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/workers", { params });
      setWorkers(data);
    } catch (_) { setWorkers([]); }
  }, [selectedCompanyId, tab, search]);

  const loadExpiring = useCallback(async () => {
    try {
      const { data } = await api.get("/workers/expiring", { params: { days: 60 } });
      setExpiring(data);
    } catch (_) { setExpiring([]); }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadWorkers(); }, [loadWorkers]);
  useEffect(() => { loadExpiring(); }, [loadExpiring]);
  useEffect(() => {
    const t = setInterval(() => { loadCompanies(); loadExpiring(); }, POLL_MS);
    return () => clearInterval(t);
  }, [loadCompanies, loadExpiring]);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompanyId),
    [companies, selectedCompanyId]
  );

  const totalExpiring = expiring.length;
  const urgentExpiring = expiring.filter((w) =>
    (w.items || []).some((i) => i.days_left <= 30)
  ).length;

  const onCreated = () => { setAddOpen(false); loadWorkers(); loadCompanies(); loadExpiring(); };
  const onUpdated = () => { setEditTarget(null); loadWorkers(); loadCompanies(); loadExpiring(); };
  const onDeleted = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/workers/${deleteTarget.id}`);
      toast.success(`${deleteTarget.full_name} removed`);
      setDeleteTarget(null);
      setEditTarget(null);
      loadWorkers(); loadCompanies(); loadExpiring();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // Non-owner request flow: submit reason → owner approves later.
  const submitDeletionRequest = async (reason) => {
    if (!requestDeleteTarget || !reason.trim()) return;
    try {
      await api.post(`/workers/${requestDeleteTarget.id}/request-deletion`, { reason: reason.trim() });
      toast.success("Deletion request sent to owner");
      setRequestDeleteTarget(null);
      setEditTarget(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
  };

  // Owner: approve/reject pending request.
  const reviewDeletionRequest = async (note) => {
    if (!reviewTarget) return;
    const { req, mode } = reviewTarget;
    try {
      await api.post(`/worker-deletion-requests/${req.id}/${mode}`, { note: note || "" });
      toast.success(mode === "approve" ? "Approved — worker deleted" : "Rejected");
      setReviewTarget(null);
      loadDeletionRequests(); loadWorkers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Review failed"); }
  };

  useEffect(() => { loadDeletionRequests(); }, [loadDeletionRequests]);

  return (
    <Layout>
      <div className="px-3 sm:px-5 py-4 max-w-[1600px] mx-auto" data-testid="workers-register-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold flex items-center gap-2">
              <IdCard className="w-5 h-5 text-violet-400" /> Workers Register
            </h1>
            <p className="text-[11px] text-slate-500 mt-0.5">
              HR records, salaries, visa & permits — by company.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {totalExpiring > 0 && (
              <button
                type="button"
                onClick={() => setTab("expiring")}
                className={`px-2.5 py-1.5 rounded-md text-xs num inline-flex items-center gap-1.5
                  ${urgentExpiring > 0
                    ? "bg-rose-500/10 border border-rose-500/40 text-rose-300 hover:bg-rose-500/20"
                    : "bg-amber-500/10 border border-amber-500/40 text-amber-200 hover:bg-amber-500/20"}`}
                data-testid="expiring-banner"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {urgentExpiring > 0
                  ? `${urgentExpiring} document(s) expire in <30 days`
                  : `${totalExpiring} document(s) expire in <60 days`}
              </button>
            )}
          </div>
        </div>

        {/* Owner-only: pending worker-deletion requests from staff */}
        {isOwner && deletionRequests.length > 0 && (
          <div className="mb-3 rounded-lg border border-rose-500/40 bg-rose-500/5 p-3"
               data-testid="pending-deletion-requests">
            <div className="text-sm font-medium text-rose-200 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" />
              {deletionRequests.length} pending worker deletion request{deletionRequests.length !== 1 ? "s" : ""}
            </div>
            <div className="space-y-2">
              {deletionRequests.map((r) => (
                <div key={r.id} className="rounded border border-slate-800 bg-slate-900/40 p-2.5"
                     data-testid={`deletion-req-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-slate-100 font-medium">{r.worker_name}</span>
                        <span className="text-[11px] text-slate-500 num">· {r.worker_company || "—"}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 num mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full" style={{ background: r.requested_by_color || "#64748b" }} />
                          {r.requested_by_name}
                        </span>
                        <span className="text-slate-600">·</span>
                        <Clock className="w-3 h-3 inline" /> {(r.requested_at || "").slice(0, 16).replace("T", " ")}
                      </div>
                      <div className="text-xs text-slate-300 italic mt-1">"{r.reason}"</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" onClick={() => setReviewTarget({ mode: "approve", req: r })}
                        className="h-8 bg-rose-600 hover:bg-rose-500"
                        data-testid={`deletion-req-approve-${r.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setReviewTarget({ mode: "reject", req: r })}
                        className="h-8 border-slate-600 text-slate-200 hover:bg-slate-800"
                        data-testid={`deletion-req-reject-${r.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3">
          {/* Left rail — companies */}
          <aside className={`${mobileMenuOpen ? "block" : "hidden"} lg:block`}>
            <div className="rounded-md border hairline surface-1 overflow-hidden">
              <div className="px-3 py-2 border-b hairline text-[11px] uppercase tracking-wider text-slate-500 num">
                Companies
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {companies.length === 0 ? (
                  <div className="px-3 py-6 text-xs text-slate-500 text-center">
                    No companies yet.
                  </div>
                ) : companies.map((c) => (
                  <button key={c.id}
                    type="button"
                    onClick={() => { setSelectedCompanyId(c.id); setMobileMenuOpen(false); }}
                    className={`w-full text-left px-3 py-2 border-b hairline text-sm transition-colors
                      ${selectedCompanyId === c.id ? "bg-violet-500/10 text-violet-100" : "hover:bg-white/[0.04] text-slate-300"}`}
                    data-testid={`company-${c.name.toLowerCase().replace(/\s+/g,'-')}`}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <span className="truncate flex-1">{c.name}</span>
                      {c.expiring_soon > 0 && (
                        <span className="num text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          {c.expiring_soon}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 num mt-1">
                      <span>{c.total} total</span>
                      {c.foreigners > 0 && <span>· {c.foreigners} foreign</span>}
                      {c.locals > 0 && <span>· {c.locals} local</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main — workers list */}
          <main>
            <div className="rounded-md border hairline surface-1 overflow-hidden">
              <div className="px-3 py-2.5 border-b hairline flex items-center gap-2 flex-wrap">
                <button type="button"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-1 text-slate-400 hover:text-slate-200">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="text-sm font-medium flex-1 truncate">
                  {selectedCompany?.name || "Select a company"}
                </div>
                <div className="relative w-44 sm:w-64">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-500" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, IC, passport…"
                    className="bg-[#0a0e1a] border-slate-800 pl-7 h-8 text-xs"
                    data-testid="workers-search" />
                </div>
                <Button size="sm" onClick={() => setAddOpen(true)}
                  disabled={!selectedCompanyId}
                  className="bg-violet-600 hover:bg-violet-500 h-8 gap-1"
                  data-testid="add-worker-btn">
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-transparent border-b hairline rounded-none w-full justify-start px-3 h-9">
                  <TabsTrigger value="foreigner" data-testid="tab-foreigner"
                    className="data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-200 text-xs">
                    Foreigners
                  </TabsTrigger>
                  <TabsTrigger value="local" data-testid="tab-local"
                    className="data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-200 text-xs">
                    Locals
                  </TabsTrigger>
                  <TabsTrigger value="expiring" data-testid="tab-expiring"
                    className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-200 text-xs gap-1.5">
                    Expiring Soon
                    {totalExpiring > 0 && (
                      <span className="num text-[10px] px-1.5 rounded bg-amber-500/20 text-amber-200">
                        {totalExpiring}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="foreigner" className="mt-0">
                  <WorkersTable workers={workers} variant="foreigner"
                    onClick={(w) => setEditTarget(w)} />
                </TabsContent>
                <TabsContent value="local" className="mt-0">
                  <WorkersTable workers={workers} variant="local"
                    onClick={(w) => setEditTarget(w)} />
                </TabsContent>
                <TabsContent value="expiring" className="mt-0">
                  <ExpiringTable rows={expiring} companies={companies}
                    onClick={async (w) => {
                      try {
                        const { data } = await api.get(`/workers/${w.id}`);
                        setEditTarget(data);
                      } catch (_) { /* silent */ }
                    }} />
                </TabsContent>
              </Tabs>
            </div>
          </main>
        </div>

        {/* Add dialog */}
        {addOpen && (
          <WorkerFormDialog
            open={addOpen}
            onOpenChange={(v) => !v && setAddOpen(false)}
            companies={companies}
            initialCompanyId={selectedCompanyId}
            initialType={tab === "local" ? "local" : "foreigner"}
            onDone={onCreated}
          />
        )}

        {/* Edit dialog with documents */}
        {editTarget && (
          <WorkerFormDialog
            open={!!editTarget}
            onOpenChange={(v) => !v && setEditTarget(null)}
            companies={companies}
            target={editTarget}
            isAdmin={isAdmin}
            isOwner={isOwner}
            onDone={onUpdated}
            onAskDelete={() => {
              // Owner → direct delete confirm. Others → request-deletion dialog.
              if (isOwner) setDeleteTarget(editTarget);
              else setRequestDeleteTarget(editTarget);
            }}
          />
        )}

        {/* Delete confirmation (owner-only direct delete) */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
          <AlertDialogContent className="bg-[#0f1626] border-slate-800 text-slate-100">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove this worker?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                <span className="text-slate-200">{deleteTarget?.full_name}</span> and all their
                uploaded documents (photo, passport, permit, IC) will be permanently deleted.
                This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDeleted}
                className="bg-rose-600 hover:bg-rose-500"
                data-testid="confirm-delete-worker">
                Delete worker
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Deletion-request dialog (non-owner submits reason) */}
        {requestDeleteTarget && (
          <RequestDeletionDialog
            worker={requestDeleteTarget}
            onClose={() => setRequestDeleteTarget(null)}
            onSubmit={submitDeletionRequest}
          />
        )}

        {/* Review dialog (owner approves/rejects a pending request) */}
        {reviewTarget && (
          <ReviewDeletionDialog
            request={reviewTarget.req}
            mode={reviewTarget.mode}
            onClose={() => setReviewTarget(null)}
            onSubmit={reviewDeletionRequest}
          />
        )}
      </div>
    </Layout>
  );
}

// ---------------- Request deletion dialog (non-owner) ----------------
function RequestDeletionDialog({ worker, onClose, onSubmit }) {
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please give a reason"); return; }
    setBusy(true);
    await onSubmit(reason);
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100" data-testid="request-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Request worker deletion
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded border border-slate-800 p-2 text-[11px] num text-slate-400 bg-slate-900/40">
            Worker: <span className="text-slate-200">{worker.full_name}</span>
            <div className="text-slate-500">{worker.company_name || "—"}</div>
          </div>
          <Field label="Reason for deletion (required) *">
            <Textarea rows={4} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Worker terminated 3 months ago, no return. Passport returned."
              data-testid="request-delete-reason" />
          </Field>
          <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
            ⚠ This does NOT delete the worker. The owner will review and either approve
            (permanent delete + all documents) or reject with a note.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim()}
            className="bg-amber-600 hover:bg-amber-500"
            data-testid="request-delete-submit">
            {busy ? "Sending…" : "Send request to owner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Review deletion dialog (owner) ----------------
function ReviewDeletionDialog({ request, mode, onClose, onSubmit }) {
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    setBusy(true);
    await onSubmit(note);
    setBusy(false);
  };
  const isApprove = mode === "approve";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100"
        data-testid={`review-delete-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle>
            {isApprove ? "Approve deletion?" : "Reject deletion?"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded border border-slate-800 p-2 text-[11px] num text-slate-400 bg-slate-900/40">
            <div>Worker: <span className="text-slate-200">{request.worker_name}</span>
              <span className="text-slate-500"> · {request.worker_company || "—"}</span>
            </div>
            <div className="mt-1">Requested by: <span className="text-slate-300">{request.requested_by_name}</span>
              <span className="text-slate-600"> · {(request.requested_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="mt-1 text-slate-200 italic">"{request.reason}"</div>
          </div>
          {isApprove && (
            <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2">
              ⚠ Approving will permanently delete the worker record AND every uploaded
              document (photo, passport, permit, IC). This cannot be undone.
            </div>
          )}
          <Field label={isApprove ? "Note (optional)" : "Reason for rejection (recommended)"}>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={isApprove ? "Anything to record" : "Why aren't we deleting?"}
              data-testid="review-delete-note" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className={isApprove ? "bg-rose-600 hover:bg-rose-500" : "bg-slate-600 hover:bg-slate-500"}
            data-testid={`review-delete-${mode}-submit`}>
            {busy ? "Working…" : (isApprove ? "Approve + Delete" : "Reject")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- WorkersTable ----------------
function WorkersTable({ workers, variant, onClick }) {
  if (!workers.length) {
    return <div className="text-sm text-slate-500 text-center py-12">No {variant === "local" ? "locals" : "foreigners"} registered yet.</div>;
  }
  return (
    <div className="divide-y divide-slate-800">
      {workers.map((w) => {
        const expired = (variant === "foreigner") && expiryFlag(w);
        return (
          <button key={w.id} type="button"
            onClick={() => onClick(w)}
            className="w-full text-left px-3 py-2.5 hover:bg-white/[0.03] transition-colors flex items-center gap-3"
            data-testid={`worker-row-${w.id}`}>
            <Avatar workerId={w.id} hasPhoto={w.has_photo} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{w.full_name}</div>
              <div className="flex items-center gap-2 text-[11px] text-slate-500 num mt-0.5 flex-wrap">
                {w.code && <span>#{w.code}</span>}
                {w.position && <span className="truncate">· {w.position}</span>}
                {w.basic_salary > 0 && <span>· RM {Number(w.basic_salary).toFixed(2)}</span>}
                {variant === "foreigner" && w.nationality && <span>· {w.nationality}</span>}
                {variant === "foreigner" && w.passport_number && <span>· passport {w.passport_number}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {expired && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded num ${expired.tone}`}>
                  {expired.label}
                </span>
              )}
              <span className={`text-[10px] px-2 py-0.5 rounded num border ${STATUS_COLOR[w.status] || STATUS_COLOR.active}`}>
                {STATUS_LABEL[w.status] || w.status}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function expiryFlag(w) {
  // Find soonest expiry across passport/permit/visa
  const today = new Date(); today.setHours(0,0,0,0);
  const items = ["passport_expiry", "permit_expiry", "visa_expiry"]
    .map((k) => w[k]).filter(Boolean);
  if (!items.length) return null;
  let minDays = 9999;
  for (const d of items) {
    const dt = new Date(d);
    if (isNaN(dt)) continue;
    const days = Math.round((dt - today) / 86400000);
    if (days < minDays) minDays = days;
  }
  if (minDays > 60) return null;
  if (minDays < 0) return { label: "EXPIRED", tone: "bg-rose-600/30 text-rose-100 border border-rose-500/60" };
  if (minDays <= 30) return { label: `${minDays}d`, tone: "bg-rose-500/15 text-rose-300 border border-rose-500/30" };
  return { label: `${minDays}d`, tone: "bg-amber-500/15 text-amber-300 border border-amber-500/30" };
}

// ---------------- Avatar (uses worker photo if available) ----------------
function Avatar({ workerId, hasPhoto }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!hasPhoto) { setUrl(""); return; }
    let cancelled = false; let cleanup = "";
    api.get(`/workers/${workerId}/document/photo`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        cleanup = URL.createObjectURL(r.data);
        setUrl(cleanup);
      })
      .catch(() => {});
    return () => { cancelled = true; if (cleanup) URL.revokeObjectURL(cleanup); };
  }, [workerId, hasPhoto]);
  if (url) {
    return <img src={url} alt="" className="w-8 h-8 rounded-full object-cover bg-slate-800 shrink-0" />;
  }
  return (
    <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
      <Camera className="w-3.5 h-3.5 text-slate-600" />
    </div>
  );
}

// ---------------- Expiring Table ----------------
function ExpiringTable({ rows, companies, onClick }) {
  if (!rows.length) {
    return <div className="text-sm text-slate-500 text-center py-12">No documents expiring in the next 60 days.</div>;
  }
  const cmap = Object.fromEntries(companies.map((c) => [c.id, c.name]));
  return (
    <div className="divide-y divide-slate-800">
      {rows.map((w) => (
        <button key={w.id} type="button"
          onClick={() => onClick(w)}
          className="w-full text-left px-3 py-2.5 hover:bg-white/[0.03] transition-colors"
          data-testid={`expiring-row-${w.id}`}>
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{w.full_name}</div>
              <div className="flex items-center gap-2 text-[11px] text-slate-500 num mt-0.5 flex-wrap">
                <span className="truncate">{cmap[w.company_id] || ""}</span>
                {w.passport_number && <span>· passport {w.passport_number}</span>}
                {w.phone && <span>· {w.phone}</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 max-w-[55%] justify-end">
              {(w.items || []).map((it) => {
                const tone = it.days_left < 0
                  ? "bg-rose-600/30 text-rose-100 border-rose-500/60"
                  : it.days_left <= 30
                  ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
                  : "bg-amber-500/15 text-amber-300 border-amber-500/30";
                return (
                  <span key={it.kind}
                    className={`text-[10px] px-2 py-0.5 rounded num border ${tone}`}>
                    {it.kind}: {it.expiry} ({it.days_left}d)
                  </span>
                );
              })}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ---------------- Worker create / edit dialog ----------------
function WorkerFormDialog({ open, onOpenChange, companies, target, initialCompanyId,
                           initialType, isAdmin, isOwner, onDone, onAskDelete }) {
  const isEdit = !!target;
  const [form, setForm] = useState(() => ({
    company_id: target?.company_id || initialCompanyId || "",
    worker_type: target?.worker_type || initialType || "foreigner",
    full_name: target?.full_name || "",
    code: target?.code || "",
    nickname: target?.nickname || "",
    ic_number: target?.ic_number || "",
    passport_number: target?.passport_number || "",
    nationality: target?.nationality || "",
    date_of_birth: target?.date_of_birth || "",
    gender: target?.gender || "",
    marital_status: target?.marital_status || "",
    religion: target?.religion || "",
    race: target?.race || "",
    phone: target?.phone || "",
    whatsapp: target?.whatsapp || "",
    email: target?.email || "",
    address: target?.address || "",
    emergency_contact_name: target?.emergency_contact_name || "",
    emergency_contact_phone: target?.emergency_contact_phone || "",
    emergency_contact_relation: target?.emergency_contact_relation || "",
    position: target?.position || "",
    department: target?.department || "",
    join_date: target?.join_date || "",
    probation_end_date: target?.probation_end_date || "",
    contract_end_date: target?.contract_end_date || "",
    status: target?.status || "active",
    basic_salary: target?.basic_salary || 0,
    allowance: target?.allowance || 0,
    ot_rate: target?.ot_rate || 0,
    bank_name: target?.bank_name || "",
    bank_account: target?.bank_account || "",
    visa_type: target?.visa_type || "",
    visa_expiry: target?.visa_expiry || "",
    permit_number: target?.permit_number || "",
    permit_expiry: target?.permit_expiry || "",
    passport_expiry: target?.passport_expiry || "",
    fomema_date: target?.fomema_date || "",
    fomema_next_due: target?.fomema_next_due || "",
    levy_paid_date: target?.levy_paid_date || "",
    country_of_origin: target?.country_of_origin || "",
    agent_name: target?.agent_name || "",
    notes: target?.notes || "",
  }));
  const [busy, setBusy] = useState(false);

  const isForeigner = form.worker_type === "foreigner";

  const submit = async () => {
    if (!form.company_id) { toast.error("Pick a company"); return; }
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setBusy(true);
    try {
      // Send only fields backend expects (drop empty strings on PUT to keep
      // existing values? — backend accepts them all; sending empties is fine
      // because Pydantic Update treats only None as 'don't update').
      const payload = { ...form };
      if (isEdit) {
        await api.put(`/workers/${target.id}`, payload);
        toast.success("Saved");
      } else {
        await api.post("/workers", payload);
        toast.success("Worker added");
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto p-0"
        data-testid="worker-form-dialog">
        <DialogHeader className="px-5 pt-4 pb-2 border-b hairline">
          <DialogTitle className="flex items-center gap-2">
            <IdCard className="w-4 h-4 text-violet-400" />
            {isEdit ? `Edit: ${target.full_name}` : "Add worker"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Worker registration form with personal, employment, salary,
            {isForeigner ? " visa/permit," : ""} and document fields.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4">
          <Tabs defaultValue="personal">
            <TabsList className="bg-transparent border-b hairline rounded-none w-full justify-start h-9 px-0">
              <TabsTrigger value="personal" className="text-xs">Personal</TabsTrigger>
              <TabsTrigger value="employment" className="text-xs">Employment</TabsTrigger>
              <TabsTrigger value="salary" className="text-xs">Salary & Bank</TabsTrigger>
              {isForeigner && <TabsTrigger value="visa" className="text-xs">Visa & Permit</TabsTrigger>}
              {isEdit && <TabsTrigger value="docs" className="text-xs">Documents</TabsTrigger>}
            </TabsList>

            {/* -------- Personal tab -------- */}
            <TabsContent value="personal" className="mt-3 space-y-3">
              <Row2>
                <Field label="Company *">
                  <Select value={form.company_id}
                    onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9"
                      data-testid="company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1626] border-slate-800">
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Type *">
                  <Select value={form.worker_type}
                    onValueChange={(v) => setForm({ ...form, worker_type: v })}>
                    <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1626] border-slate-800">
                      <SelectItem value="foreigner">Foreigner</SelectItem>
                      <SelectItem value="local">Local</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </Row2>

              <Row2>
                <Field label="Full name *">
                  <Input value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9"
                    data-testid="full-name" />
                </Field>
                <Field label="Nickname">
                  <Input value={form.nickname}
                    onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
              </Row2>

              <Row2>
                <Field label={isForeigner ? "Passport number" : "IC number"}>
                  <Input value={isForeigner ? form.passport_number : form.ic_number}
                    onChange={(e) => setForm(isForeigner
                      ? { ...form, passport_number: e.target.value }
                      : { ...form, ic_number: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                </Field>
                <Field label="Nationality">
                  <Select value={form.nationality}
                    onValueChange={(v) => setForm({ ...form, nationality: v })}>
                    <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1626] border-slate-800">
                      {NATIONALITIES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </Row2>

              <Row2>
                <Field label="Date of birth">
                  <Input type="date" value={form.date_of_birth}
                    onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
                <Field label="Gender">
                  <Select value={form.gender}
                    onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1626] border-slate-800">
                      <SelectItem value="M">Male</SelectItem>
                      <SelectItem value="F">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </Row2>

              <Row2>
                <Field label="Marital status">
                  <Input value={form.marital_status}
                    onChange={(e) => setForm({ ...form, marital_status: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
                <Field label="Religion / race">
                  <Input value={form.religion}
                    onChange={(e) => setForm({ ...form, religion: e.target.value })}
                    placeholder="e.g. Islam"
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
              </Row2>

              <Row2>
                <Field label="Phone">
                  <Input value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                </Field>
                <Field label="WhatsApp">
                  <Input value={form.whatsapp}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                </Field>
              </Row2>

              <Field label="Email">
                <Input value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-[#0a0e1a] border-slate-800 h-9" />
              </Field>

              <Field label="Address">
                <textarea value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2}
                  className="w-full bg-[#0a0e1a] border border-slate-800 rounded-md px-3 py-2 text-sm" />
              </Field>

              <div className="rounded-md border hairline p-3 bg-[#0a0e1a]/40">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 num mb-2">Emergency contact</div>
                <Row3>
                  <Input placeholder="Name" value={form.emergency_contact_name}
                    onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                  <Input placeholder="Phone" value={form.emergency_contact_phone}
                    onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                  <Input placeholder="Relation (e.g. spouse)" value={form.emergency_contact_relation}
                    onChange={(e) => setForm({ ...form, emergency_contact_relation: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Row3>
              </div>
            </TabsContent>

            {/* -------- Employment tab -------- */}
            <TabsContent value="employment" className="mt-3 space-y-3">
              <Row2>
                <Field label="Position / job title">
                  <Input value={form.position}
                    onChange={(e) => setForm({ ...form, position: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
                <Field label="Department">
                  <Input value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
              </Row2>
              <Row2>
                <Field label="Employee code / payroll id">
                  <Input value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                </Field>
                <Field label="Status">
                  <Select value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1626] border-slate-800">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </Row2>
              <Row3>
                <Field label="Join date">
                  <Input type="date" value={form.join_date}
                    onChange={(e) => setForm({ ...form, join_date: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
                <Field label="Probation end">
                  <Input type="date" value={form.probation_end_date}
                    onChange={(e) => setForm({ ...form, probation_end_date: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
                <Field label="Contract end">
                  <Input type="date" value={form.contract_end_date}
                    onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
              </Row3>
              <Field label="Notes / remarks">
                <textarea value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-[#0a0e1a] border border-slate-800 rounded-md px-3 py-2 text-sm" />
              </Field>
            </TabsContent>

            {/* -------- Salary tab -------- */}
            <TabsContent value="salary" className="mt-3 space-y-3">
              <Row3>
                <Field label="Basic salary (RM)">
                  <Input type="number" min="0" step="0.01" value={form.basic_salary}
                    onChange={(e) => setForm({ ...form, basic_salary: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                </Field>
                <Field label="Allowance (RM)">
                  <Input type="number" min="0" step="0.01" value={form.allowance}
                    onChange={(e) => setForm({ ...form, allowance: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                </Field>
                <Field label="OT rate (RM/hr)">
                  <Input type="number" min="0" step="0.01" value={form.ot_rate}
                    onChange={(e) => setForm({ ...form, ot_rate: parseFloat(e.target.value) || 0 })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                </Field>
              </Row3>
              <Row2>
                <Field label="Bank name">
                  <Input value={form.bank_name}
                    onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
                <Field label="Bank account number">
                  <Input value={form.bank_account}
                    onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                </Field>
              </Row2>
            </TabsContent>

            {/* -------- Visa & Permit tab -------- */}
            {isForeigner && (
              <TabsContent value="visa" className="mt-3 space-y-3">
                <div className="text-[11px] text-slate-500 italic">
                  Reminders trigger 60 days and 30 days before expiry. Enter dates here, then upload the scanned page in the Documents tab.
                </div>
                <Row2>
                  <Field label="Visa type">
                    <Input value={form.visa_type}
                      onChange={(e) => setForm({ ...form, visa_type: e.target.value })}
                      placeholder="e.g. FWP, VPP" className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                  </Field>
                  <Field label="Visa expiry">
                    <Input type="date" value={form.visa_expiry}
                      onChange={(e) => setForm({ ...form, visa_expiry: e.target.value })}
                      className="bg-[#0a0e1a] border-slate-800 h-9" />
                  </Field>
                </Row2>
                <Row2>
                  <Field label="Work permit number">
                    <Input value={form.permit_number}
                      onChange={(e) => setForm({ ...form, permit_number: e.target.value })}
                      className="bg-[#0a0e1a] border-slate-800 h-9 num" />
                  </Field>
                  <Field label="Permit expiry">
                    <Input type="date" value={form.permit_expiry}
                      onChange={(e) => setForm({ ...form, permit_expiry: e.target.value })}
                      className="bg-[#0a0e1a] border-slate-800 h-9" />
                  </Field>
                </Row2>
                <Field label="Passport expiry">
                  <Input type="date" value={form.passport_expiry}
                    onChange={(e) => setForm({ ...form, passport_expiry: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
                <Row2>
                  <Field label="FOMEMA last">
                    <Input type="date" value={form.fomema_date}
                      onChange={(e) => setForm({ ...form, fomema_date: e.target.value })}
                      className="bg-[#0a0e1a] border-slate-800 h-9" />
                  </Field>
                  <Field label="FOMEMA next due">
                    <Input type="date" value={form.fomema_next_due}
                      onChange={(e) => setForm({ ...form, fomema_next_due: e.target.value })}
                      className="bg-[#0a0e1a] border-slate-800 h-9" />
                  </Field>
                </Row2>
                <Row2>
                  <Field label="Levy paid date">
                    <Input type="date" value={form.levy_paid_date}
                      onChange={(e) => setForm({ ...form, levy_paid_date: e.target.value })}
                      className="bg-[#0a0e1a] border-slate-800 h-9" />
                  </Field>
                  <Field label="Country of origin">
                    <Input value={form.country_of_origin}
                      onChange={(e) => setForm({ ...form, country_of_origin: e.target.value })}
                      className="bg-[#0a0e1a] border-slate-800 h-9" />
                  </Field>
                </Row2>
                <Field label="Agent / supplier">
                  <Input value={form.agent_name}
                    onChange={(e) => setForm({ ...form, agent_name: e.target.value })}
                    className="bg-[#0a0e1a] border-slate-800 h-9" />
                </Field>
              </TabsContent>
            )}

            {/* -------- Documents tab (only when editing) -------- */}
            {isEdit && (
              <TabsContent value="docs" className="mt-3">
                <DocumentsPanel worker={target} isAdmin={isAdmin} onChanged={() => onDone?.()} />
              </TabsContent>
            )}
          </Tabs>
        </div>

        <DialogFooter className="px-5 py-3 border-t hairline gap-2">
          {isEdit && isOwner && (
            <Button variant="ghost"
              className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 mr-auto"
              onClick={() => onAskDelete?.()}
              data-testid="ask-delete-worker">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete worker
            </Button>
          )}
          {isEdit && !isOwner && (
            <Button variant="ghost"
              className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 mr-auto"
              onClick={() => onAskDelete?.()}
              data-testid="ask-request-delete-worker"
              title="Owner must approve — you'll get a WhatsApp when they decide.">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Request deletion
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-violet-600 hover:bg-violet-500"
            data-testid="save-worker">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Add worker")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Documents Panel ----------------
function DocumentsPanel({ worker, isAdmin, onChanged }) {
  const isForeigner = worker.worker_type === "foreigner";
  // photo + (passport or ic) + permit (foreigners only) + ic (locals only)
  const docs = isForeigner
    ? [
        { kind: "photo", label: "Photo", icon: Camera, accept: "image/*", resize: true },
        { kind: "passport", label: "Passport page", icon: ShieldCheck, accept: "image/*,application/pdf" },
        { kind: "permit", label: "Work permit", icon: BadgeCheck, accept: "image/*,application/pdf" },
      ]
    : [
        { kind: "photo", label: "Photo", icon: Camera, accept: "image/*", resize: true },
        { kind: "ic", label: "IC scan", icon: IdCard, accept: "image/*" },
      ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {docs.map((d) => (
        <DocumentCard key={d.kind} workerId={worker.id} workerHas={!!worker[`has_${d.kind}`]}
          kind={d.kind} label={d.label} Icon={d.icon} accept={d.accept}
          isAdmin={isAdmin} onChanged={onChanged} resize={d.resize} />
      ))}
    </div>
  );
}

async function resizeImageFile(file, maxW = 1024, maxH = 1024, quality = 0.7) {
  if (!file.type.startsWith("image/")) return file;
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl;
  });
  const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
  const w = Math.round(img.width * ratio), h = Math.round(img.height * ratio);
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  c.getContext("2d").drawImage(img, 0, 0, w, h);
  const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", quality));
  return new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
}

function DocumentCard({ workerId, workerHas, kind, label, Icon, accept, isAdmin, onChanged, resize }) {
  const [url, setUrl] = useState("");
  const [contentType, setContentType] = useState("");
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);

  useEffect(() => {
    if (!workerHas) { setUrl(""); return; }
    let cancelled = false; let cleanup = "";
    api.get(`/workers/${workerId}/document/${kind}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        cleanup = URL.createObjectURL(r.data);
        setUrl(cleanup);
        setContentType(r.data.type || "");
      })
      .catch(() => {});
    return () => { cancelled = true; if (cleanup) URL.revokeObjectURL(cleanup); };
  }, [workerId, kind, workerHas, bust]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const final = resize ? await resizeImageFile(f) : f;
      const fd = new FormData();
      fd.append("file", final);
      await api.post(`/workers/${workerId}/document/${kind}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      setBust(Date.now());
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      await api.delete(`/workers/${workerId}/document/${kind}`);
      toast.success(`${label} deleted`);
      setUrl("");
      setBust(Date.now());
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const isPdf = contentType.includes("pdf") || (url && contentType === "" && kind !== "photo");

  return (
    <div className="rounded-md border hairline overflow-hidden surface-2"
      data-testid={`doc-card-${kind}`}>
      <div className="px-3 py-2 border-b hairline flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-violet-400" />
        <div className="text-xs font-medium flex-1">{label}</div>
        {workerHas && (
          <span className="text-[10px] num text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/30">
            on file
          </span>
        )}
      </div>
      <div className="bg-black h-40 flex items-center justify-center">
        {url ? (
          isPdf ? (
            <a href={url} target="_blank" rel="noreferrer"
              className="text-xs text-blue-300 inline-flex items-center gap-1 hover:underline">
              <FileText className="w-3.5 h-3.5" /> Open PDF in new tab
            </a>
          ) : (
            <img src={url} alt={label}
              className="max-w-full max-h-full object-contain" />
          )
        ) : (
          <div className="text-[11px] text-slate-600 flex flex-col items-center gap-1">
            <Icon className="w-6 h-6 opacity-40" />
            <span>Not uploaded</span>
          </div>
        )}
      </div>
      <div className="px-2 py-2 flex items-center gap-1.5 border-t hairline bg-[#0a0e1a]">
        <label className="flex-1">
          <input type="file" accept={accept} className="hidden"
            onChange={onFile} disabled={busy}
            data-testid={`doc-upload-${kind}`} />
          <span className="inline-flex items-center justify-center gap-1.5 w-full px-2 py-1.5 rounded-md
            bg-blue-600 hover:bg-blue-500 text-white text-xs cursor-pointer transition-colors">
            <Upload className="w-3 h-3" />
            {busy ? "Uploading…" : (workerHas ? "Replace" : "Upload")}
          </span>
        </label>
        {workerHas && isAdmin && (
          <button type="button"
            onClick={onDelete} disabled={busy}
            className="px-2 py-1.5 rounded-md text-rose-400 hover:bg-rose-500/10"
            title="Delete (admin only)"
            data-testid={`doc-delete-${kind}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------- Layout helpers ----------------
function Row2({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}
function Row3({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
